import { zodResolver } from '@hookform/resolvers/zod';
import { useContext, useState } from 'react';
import styled, { css } from 'styled-components';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { Form, NavLink, useNavigate } from 'react-router-dom';
import jwt_decode from 'jwt-decode';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';

import type { UserType } from '../../contexts';
import { getUserInfo, UserContext } from '../../contexts';
import { allPaths } from '../../routes/Paths';
import {
  InputWrapper,
  LoginFormButton,
  LoginFormFooter,
  LoginFormHeader
} from '../atoms';
import { APIEndPoints, Request } from '../../apis';
import { withNoAuthRedirect } from '../hocs/withNoAuthRedirect';
import type { SignInTO } from '../../common/types/Authentication';
import { ChallengeNames } from '../../common/types/Authentication';
import toast from 'react-hot-toast';
import type { KeyboardEventHandler } from 'react';

const loginFormSchema = z.object({
  email: z
    .string()
    .min(1, { message: 'Please enter your email address.' })
    .email('Please enter a valid email address.'),
  password: z.string().min(1, { message: 'Please enter your password.' })
});

type LoginFormSchema = z.infer<typeof loginFormSchema>;

const StyledNavLink = styled(NavLink)(
  ({ theme }) => css`
    color: ${theme.colors.darkGrey};
    font-size: ${theme.fonts.h6};
    margin-top: 15px;
    display: block;

    @media (hover: hover) {
      &:hover {
        cursor: pointer;
        color: ${theme.colors.main.secondary};
      }
    }
  `
);

const LoginPage = () => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<LoginFormSchema>({
    resolver: zodResolver(loginFormSchema)
  });

  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');
  const [initialInteraction, seInitialInteraction] = useState(true);

  // TODO move this to common
  const getSub = (decodedToken: unknown): string => {
    if (
      typeof decodedToken === 'object' &&
      decodedToken !== null &&
      'sub' in decodedToken
    ) {
      if (typeof decodedToken.sub === 'string') {
        return decodedToken.sub;
      }
    }
    return 'no-sub-found';
  };

  const {
    logOut,
    onUserChange,
    setMerchantServiceUserInfo,
    updateLoginData,
    updateSessionData
  } = useContext(UserContext);

  const handleChange: KeyboardEventHandler<HTMLInputElement> = (e) => {
    //Hide user inactivity toast
    if (initialInteraction) {
      seInitialInteraction(false);
      toast.dismiss();
    }
  };

  const onSubmit: SubmitHandler<LoginFormSchema> = async (data: any) => {
    setErrorMessage('');

    try {
      // logout first to flush any stale state, including server cookies
      await logOut();
      // set new session id early, so that it's included in initial login request
      const sessionId = uuidv4();
      updateSessionData({ sessionId });

      const response: SignInTO = await Request.post(
        `${APIEndPoints.login}/${encodeURIComponent(data.email)}`,
        z.any(),
        // SignInTOSchema, // @TODO, test if we can re-enable this schema
        {
          credentials: data.password
        }
      );

      const { signInResult, userRole, merchant } = response;
      // TODO log
      //console.log('response', JSON.stringify(response, null, 2));
      if (signInResult) {
        // const { session, challengeName } = signInResult;

        const { challengeName, session } = signInResult;

        if (challengeName === ChallengeNames.NEW_PASSWORD_REQUIRED) {
          if (!session) {
            setErrorMessage(
              'Something went wrong while signing in. Please contact support if this issue persists.'
            );
            return;
          }

          updateLoginData({
            session,
            challengeName,
            email: data.email
          });
          navigate(
            `${allPaths.CONFIRM_RESET_PASSWORD.path}?set-initial-password=true`
          );
        } else if (challengeName === ChallengeNames.SMS) {
          if (!session) {
            setErrorMessage(
              'Something went wrong while signing in. Please contact support if this issue persists.'
            );
            return;
          }

          updateLoginData({
            session,
            challengeName,
            email: data.email
          });
          navigate(allPaths.SMS_VERIFICATION.path);
        } else {
          localStorage.setItem('lastActivity', Date.now().toString());
          const { AccessToken } = signInResult;
          if (AccessToken && merchant) {
            const decodedToken = jwt_decode(AccessToken);
            const sub: string = getSub(decodedToken);

            onUserChange({
              merchantId: merchant,
              userType: userRole as UserType,
              sub: sub,
              email: data.email
            });
            const userInfo = await getUserInfo(sub);
            // TODO log
            //console.log('userInfo', userInfo);
            if (userInfo) {
              // TODO log
              //console.log('___userInfo: setMerchantServiceUserInfo');
              setMerchantServiceUserInfo(userInfo);
            } else {
              // TODO: remove this after testing / before MVP
              // TODO log
              // console.log(
              //   '___userInfo ERRROR... most likely user not found in local db'
              // );
            }
          } else {
            setErrorMessage('Something went wrong');
          }
        }
      }
    } catch (err: any) {
      // setErrorMessage(err.message); -- replaced with generic message
      setErrorMessage('Please check your username and password and try again');
    }
  };

  return (
    <div>
      <LoginFormHeader id="loginForm-h1">
        <h1>Login</h1>
      </LoginFormHeader>

      <Form
        onSubmit={handleSubmit(onSubmit)}
        noValidate={true}
        id={'loginForm'}
      >
        <InputWrapper
          id="loginForm-email"
          name="email"
          type="email"
          label="Username"
          required
          register={register}
          onKeyUp={handleChange}
          errorMessage={errors.email?.message}
          autoFill={true}
        />
        <InputWrapper
          id="loginForm-password"
          name="password"
          type="password"
          label="Password"
          required
          register={register}
          errorMessage={errorMessage || errors.password?.message}
          autoFill={true}
        />
        <LoginFormFooter
          horizontalSpacing="10px"
          verticalSpacing="0px"
          justify="center"
        >
          <LoginFormButton
            id="loginForm-loginButton"
            disabled={isSubmitting}
            type="submit"
            isLoading={isSubmitting}
          >
            <span>Login</span>
          </LoginFormButton>
        </LoginFormFooter>
      </Form>
      <StyledNavLink to={allPaths.RESET_PASSWORD.path}>
        Forgot your password?
      </StyledNavLink>
    </div>
  );
};

const SecureLoginPage = withNoAuthRedirect(LoginPage);
export { SecureLoginPage as LoginPage };
